import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Chip from '@mui/material/Chip';
import LoadingRow from '../../../components/TableBox/Rows/LoadingRow';
import Button from '../../../components/Button';
import Modal from '../../../components/Modal';
import Input from '../../../components/Input';
import Toast from '../../../components/Toast';
import TenantService from '../../../services/tenant.service';
import useSorting from '../../../hooks/useSorting';
import { sortData } from '../../../utils/sortUtils';
import { SortOrder } from '../../../enums';

interface PendingPurgeRow {
  id: number;
  tenant_name: string;
  deleted_at: string;
  retain_until: string | null;
  purge_date: string;
}

const IMMINENT_DAYS = 7;

const daysUntil = (dateStr: string): number => {
  const target = new Date(`${String(dateStr).slice(0, 10)}T00:00:00Z`).getTime();
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.ceil((target - today) / (24 * 60 * 60 * 1000));
};

const DataManagement: React.FC = () => {
  const { t } = useTranslation();
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: 'tenant_name' });
  const [items, setItems] = useState<PendingPurgeRow[]>([]);
  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);
  const [loading, setLoading] = useState(true);
  const [extendTarget, setExtendTarget] = useState<PendingPurgeRow | null>(null);
  const [retainUntil, setRetainUntil] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    TenantService.listPendingPurges()
      .then((response: any) => setItems(response?.data?.items ?? []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleExtend = useCallback(async () => {
    if (!extendTarget || !retainUntil) return;
    setSubmitting(true);
    try {
      await TenantService.setRetentionOverride(extendTarget.id, retainUntil);
      toast.success(<Toast message={t('system.dataManagement.pageTexts.extendSuccess')} />, { icon: false });
      setExtendTarget(null);
      setRetainUntil('');
      load();
    } finally {
      setSubmitting(false);
    }
  }, [extendTarget, retainUntil, load, t]);

  return (
    <div>
      <div className="mb-4">
        <h4 className="m-0">{t('system.dataManagement.pageTexts.title')}</h4>
        <p className="text-muted m-0">{t('system.dataManagement.pageTexts.subTitle')}</p>
      </div>

      <div className="card">
        <div className="card-body" style={{ overflowX: 'auto' }}>
          <table className="table table-sm">
            <thead>
              <tr>
                {[
                  { id: 'tenant_name', label: t('system.dataManagement.pageTexts.table.columns.tenantName') },
                  { id: 'deleted_at', label: t('system.dataManagement.pageTexts.table.columns.deletedAt') },
                  { id: 'purge_date', label: t('system.dataManagement.pageTexts.table.columns.purgeDate') },
                ].map((col) => {
                  const isSorted = orderBy === col.id;
                  return (
                    <th
                      key={col.id}
                      className={`sorting ${isSorted ? (order === SortOrder.asc ? 'sorting_asc' : 'sorting_desc') : ''}`}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        if (orderBy === col.id) {
                          setOrder(order === SortOrder.asc ? SortOrder.desc : SortOrder.asc);
                        } else {
                          setOrderBy(col.id);
                          setOrder(SortOrder.asc);
                        }
                      }}
                    >
                      <span className="d-inline-flex align-items-center gap-1 w-100">
                        <span>{col.label}</span>
                        <span style={{ opacity: isSorted ? 1 : 0.4, transition: 'opacity 0.15s ease' }}>
                          {isSorted ? (
                            order === SortOrder.asc ? (
                              <i className="ri-arrow-up-s-line" style={{ fontSize: '13px', fontWeight: 600 }} />
                            ) : (
                              <i className="ri-arrow-down-s-line" style={{ fontSize: '13px', fontWeight: 600 }} />
                            )
                          ) : (
                            <i className="ri-arrow-up-down-line" style={{ fontSize: '13px' }} />
                          )}
                        </span>
                      </span>
                    </th>
                  );
                })}
                <th className="text-end">{t('system.dataManagement.pageTexts.table.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <LoadingRow colSpan={4} />
              ) : sortedItems.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-muted text-center py-4">{t('system.dataManagement.pageTexts.table.noDataFoundMessage')}</td>
                </tr>
              ) : (
                sortedItems.map((row: PendingPurgeRow) => {
                  const remaining = daysUntil(row.purge_date);
                  return (
                    <tr key={row.id}>
                      <td>{row.tenant_name}</td>
                      <td>{new Date(row.deleted_at).toLocaleDateString()}</td>
                      <td>
                        {row.purge_date}
                        {remaining <= IMMINENT_DAYS && (
                          <Chip
                            size="small"
                            color="warning"
                            className="ms-2"
                            label={t('system.dataManagement.pageTexts.imminent', { count: Math.max(remaining, 0) })}
                          />
                        )}
                      </td>
                      <td className="text-end">
                        <Button
                          className="btn btn-sm btn-outline-primary"
                          text={t('system.dataManagement.pageTexts.extendAction')}
                          onClick={() => {
                            setExtendTarget(row);
                            setRetainUntil(row.retain_until ?? row.purge_date);
                          }}
                        />
                      </td>
                    </tr>
                  );
                }))}
              </tbody>
            </table>
        </div>
      </div>

      <Modal
        open={!!extendTarget}
        onClose={() => setExtendTarget(null)}
        title={t('system.dataManagement.pageTexts.modalTitle', { tenantName: extendTarget?.tenant_name ?? '' })}
        content={
          <Input
            id="retainUntil"
            name="retainUntil"
            type="date"
            label={t('system.dataManagement.pageTexts.retainUntilLabel')}
            required
            value={retainUntil}
            onChange={(e) => setRetainUntil(e.target.value)}
          />
        }
        action={
          <>
            <Button className="btn btn-outline-secondary" text={t('system.dataManagement.pageTexts.cancel')} onClick={() => setExtendTarget(null)} />
            <Button
              className="btn btn-primary"
              text={t('system.dataManagement.pageTexts.confirm')}
              loading={submitting}
              disabled={!retainUntil}
              onClick={handleExtend}
            />
          </>
        }
      />
    </div>
  );
};

export default DataManagement;
